import { Overlay, OverlayStyle } from "../../shared/overlay";
import { div, anchor, textnode, span } from "../../shared/dom";
import { App } from "./app";
import { Loading } from "../../shared/loading";
import { Ajax } from "../../shared/ajax";
import { ApiBQConnection, ApiBQTable } from "./api_data";
import { timeAgoDateFormat } from "../../shared/date_format";

export class Upload {

  static bytesSizeDescription(bytes: number) {
    const n = 1000;
    const units = Math.floor(Math.log(bytes) / Math.log(n));
    switch (units) {
      case 0:
        return bytes + " bytes";
      case 1:
        return Math.round(bytes / n) + " KB";
      default:
        return (bytes / (n * n)).toFixed(1) + " MB";
    }
  }

  static info_row(label: string, value: string): HTMLDivElement {
    const d = div("info-row");
    const l = div("info-label");
    l.innerText = label;
    const val = div("info-val");
    val.title = value;
    val.innerText = value;
    d.appendChild(l);
    d.appendChild(val);
    return d;
  }

  static input(owner_id: string): HTMLInputElement {
    const input = document.createElement("input");
    input.classList.add("upload");
    input.id = "file-input";
    input.setAttribute("name", "file");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "text/csv, .csv");
    input.onchange = function () {

      let file: File;
      const reader = new FileReader();
      reader.onload = function () {
        if (reader.result && typeof reader.result === "string" && App.user) {
          const preview = div("overlay-panel-body");
          const info = div("upload-info");
          const label = document.createElement("h3");
          label.innerText = file.name;
          info.appendChild(label);

          const date = Upload.info_row("Modified", new Date(file.lastModified).toLocaleString());
          info.appendChild(date);

          const size = Upload.info_row("Filesize", Upload.bytesSizeDescription(file.size));
          info.appendChild(size);

          const lines = reader.result.split("\n");
          const rows = Upload.info_row("Rows", lines.length.toLocaleString());
          info.appendChild(rows);
          preview.appendChild(info);

          const footer = div("overlay-panel-footer");
          footer.appendChild(div("spacer"));
          const button = document.createElement("a");
          button.classList.add("button", "noselect");
          button.innerHTML = "Upload";

          const loading = Loading.overlay();
          button.appendChild(loading);

          footer.appendChild(button);
          preview.appendChild(footer);

          new Overlay(OverlayStyle.Viz, "Upload CSV", [preview], "", function () {
            //
          });

          button.onclick = function () {
            if (button.classList.contains("working") || reader.result === null) {
              return;
            }
            button.classList.add("working");
            Overlay.disableDismissal();

            const blob = new Blob([reader.result], {});
            const data = new FormData();
            data.append("file", blob, file.name);
            Ajax.post("/api/v1/bq_upload?owner_id=" + owner_id, function (data: ApiBQTable[]) {
              App.build_table_sets(data);
              App.render();
              Overlay.enableDismissal();
              Overlay.close();
            }, data, function () {
              button.classList.remove("working");
              Overlay.enableDismissal();
            });
          }
        }
      };
      if (input.files && input.files.length > 0) {
        reader.readAsText(input.files[0]);
        file = input.files[0];
      }
      input.value = "";
    };
    return input;
  }

  static show_key_upload_overlay() {
    const body = div("overlay-panel-body");

    const desc = div("overlay-desc");
    desc.innerHTML = "To connect to Google BigQuery, upload a JSON service account key. ";
    const link = anchor();
    link.innerText = "How to create a service account key →";
    desc.appendChild(link);
    body.appendChild(desc);

    const prompt = div();
    body.appendChild(prompt);
    const info = div("upload-info", "upload-key");
    body.appendChild(info);

    const input = document.createElement("input");
    input.classList.add("upload");
    input.id = "file-input";
    input.setAttribute("name", "file");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "text/json, .json");
    desc.appendChild(input);

    input.onchange = function () {
      let file: File;
      const reader = new FileReader();
      reader.onload = function () {
        if (reader.result && typeof reader.result === "string") {
          render_info(file, reader.result);
        }
      }
      if (input.files && input.files.length > 0) {
        reader.readAsText(input.files[0]);
        file = input.files[0];
      }

      input.value = "";
    }

    const render_prompt = function (error_string: string) {
      prompt.innerHTML = "";
      info.innerHTML = "";

      if (error_string) {
        const err = div("error");
        err.innerHTML = error_string;
        prompt.appendChild(err);
      }

      const footer = div("overlay-panel-footer");
      footer.appendChild(div("spacer"));
      const button = document.createElement("a");
      button.classList.add("button", "noselect");
      button.innerHTML = "Choose File";

      const loading = Loading.overlay();
      button.appendChild(loading);

      button.onclick = function () {
        input.click();
      }

      footer.appendChild(button);
      prompt.appendChild(footer);
    }

    const render_info = function (file: File, file_text: string) {
      prompt.innerHTML = "";
      info.innerHTML = "";

      const d = JSON.parse(file_text);
      if (d.type === undefined || d.type !== "service_account" || d.project_id === undefined || d.client_email === undefined) {
        render_prompt("<b>" + file.name + "</b> doesn't appear to be a service account key.");
        return;
      }

      const label = document.createElement("h3");
      label.innerText = file.name;
      info.appendChild(label);

      const info_rows = div();
      const date = Upload.info_row("Modified", new Date(file.lastModified).toLocaleString());
      info_rows.appendChild(date);

      const id = Upload.info_row("Project ID", d.project_id);
      info_rows.appendChild(id);

      const size = Upload.info_row("Email", d.client_email);
      info_rows.appendChild(size);
      info.appendChild(info_rows);

      const footer = div("overlay-panel-footer");
      const button = document.createElement("a");
      button.classList.add("button", "noselect");
      button.innerHTML = "Connect";

      const loading = Loading.overlay();
      button.appendChild(loading);

      const select_link = anchor();
      select_link.innerText = "Choose a different file";
      select_link.href = "#";
      select_link.onclick = function (e) {
        input.click();
        e.preventDefault();
      }
      footer.appendChild(select_link);
      footer.appendChild(div("spacer"));
      footer.appendChild(button);
      info.appendChild(footer);

      button.onclick = function () {
        if (button.classList.contains("working")) {
          return;
        }
        button.classList.add("working");
        Overlay.disableDismissal();

        const blob = new Blob([file_text], {});
        const data = new FormData();
        data.append("file", blob, file.name);
        const owner_id = App.selected_owner_id;
        const path = "/api/v1/bq_connections?owner_id=" + owner_id + "&g_project_id=" + d.project_id + "&g_email=" + d.client_email;
        Ajax.post(path, function (data: ApiBQTable[]) {
          console.log(data);
          App.build_table_sets(data);
          App.render();
          Overlay.enableDismissal();
          Overlay.close();
        }, data, function () {
          button.classList.remove("working");
          Overlay.enableDismissal();
        });
      }
    }

    render_prompt("");

    new Overlay(OverlayStyle.Viz, "Connect to BigQuery", [body], "", function () {
      //
    });


  }

  static show_refresh_overlay(owner_id: string) {
    const body = div();
    const loading_contain = div("data-preview", "loading");
    const loading_el = Loading.overlay();
    loading_contain.appendChild(loading_el);
    body.appendChild(loading_contain);

    const list_contain = div();
    list_contain.style.display = "none";
    const list = div("conn-list");
    list_contain.appendChild(list);

    const footer = div("overlay-panel-footer");
    const spacer = div("spacer", "light-text");
    footer.appendChild(spacer);
    const button = document.createElement("a");
    button.classList.add("button", "noselect");
    button.innerHTML = "Refresh Schemas";
    const loading = Loading.overlay();
    button.appendChild(loading);
    footer.appendChild(button);
    list_contain.appendChild(footer);
    body.appendChild(list_contain);

    button.onclick = function () {
      button.classList.add("working");
      spacer.innerText = "";
      Ajax.post("/api/v1/bq_connections/" + owner_id + "/refresh", function () {
        App.get_user_data(function () {
          button.classList.remove("working");
          spacer.innerText = "Table schemas updated.";
          App.render();
        });
      }, undefined, function () {
        button.classList.remove("working");
      })
    }

    const render_list = function (data: ApiBQConnection[]) {
      for (const c of data) {
        const d = div("conn", "light-text");
        const label = document.createElement("h3");
        label.innerText = c.file_name;
        d.appendChild(label);
        const info = div();
        info.innerText = c.project_id;
        const dot = span("dot");
        dot.innerText = " • ";
        info.appendChild(dot);
        const updated = timeAgoDateFormat(new Date(c.created_at));
        info.appendChild(textnode(updated));
        d.appendChild(info);
        list.appendChild(d);
      }
      loading_contain.style.display = "none";
      list_contain.style.display = "block";
    }

    Ajax.get("/api/v1/bq_connections/" + owner_id, function (data: ApiBQConnection[]) {
      render_list(data);
    });

    new Overlay(OverlayStyle.Viz, "Edit Connections", [body], "", function () {
      //
    });
  }

}